/**
 * Pending Action Storage Utility
 * Stores and retrieves pending actions through the authentication flow
 */

const PENDING_ACTION_KEY = 'evee_pending_action';
const DEFAULT_EXPIRATION_MS = 60 * 60 * 1000; // 1 hour

export type PendingActionType = 
  | 'start_ai_interview'
  | 'start_quiz' 
  | 'book_interview'
  | 'start_assignment'
  | 'view_application'
  | 'view_stage'
  | 'start_stage'
  | 'start'
  | string;

export interface PendingAction {
  // Core identifiers
  type: PendingActionType;
  applicationId: string;
  stageId?: string;
  
  // Additional context
  jobId?: string;
  stageType?: string;
  stageConfig?: Record<string, unknown>;
  
  // Navigation
  returnPath: string;
  
  // Tracking
  source?: 'email' | 'notification' | 'direct';
  createdAt: number;
  expiresAt: number;
}

/**
 * Store a pending action before auth redirect
 */
export function storePendingAction(action: Omit<PendingAction, 'createdAt' | 'expiresAt'> & { expiresAt?: number }): void {
  const now = Date.now();
  const pendingAction: PendingAction = {
    ...action,
    createdAt: now,
    expiresAt: action.expiresAt || now + DEFAULT_EXPIRATION_MS
  };
  
  try {
    localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(pendingAction));
    console.log('[PendingAction] Stored:', pendingAction);
  } catch (error) {
    console.error('[PendingAction] Failed to store:', error);
  }
}

/**
 * Retrieve pending action (does not clear it)
 */
export function getPendingAction(): PendingAction | null {
  try {
    const stored = localStorage.getItem(PENDING_ACTION_KEY);
    if (!stored) return null;
    
    const action = JSON.parse(stored) as PendingAction;
    
    // Check expiration
    if (!isPendingActionValid(action)) {
      console.log('[PendingAction] Expired, clearing');
      clearPendingAction();
      return null;
    }
    
    return action;
  } catch (error) {
    console.error('[PendingAction] Failed to retrieve:', error);
    return null;
  }
}

/**
 * Clear the pending action
 */
export function clearPendingAction(): void {
  try {
    localStorage.removeItem(PENDING_ACTION_KEY);
    console.log('[PendingAction] Cleared');
  } catch (error) {
    console.error('[PendingAction] Failed to clear:', error);
  }
}

/**
 * Check if a pending action is still valid (not expired)
 */
export function isPendingActionValid(action: PendingAction): boolean {
  return Date.now() < action.expiresAt;
}
